import { PaginationInfo, removeDuplicateQueryParams } from "@robe/core-utils";
import { Content } from "@robe/core-types";
import { XcoreCore } from "XcoreCore";
import { GetContentsOptions, GetContentsOptionsBase, GetContentsSource, QueryOptionsBase } from "./types";
import { BaseApiRequest } from "@robe/core-api/baseApiRequest";
import { paginateRes, PaginationParsed, resolvePagination } from "./utils/pagination";
import { getContentsUrl as getContentsUrlFunc } from "./getContents";
import { resolveUnfetchedValuesToContentValuesForContents } from "./utils/resolveAndAssignUnfetchedValues";
import { resolveLocaleParams } from "@robe/core-api/content/utils/queryParamsResolvers";
import { resolveContentsSubCategories } from "./utils/subCategories";

/** Used in `listByIds` and `listByTypes` */
export interface MixedContentsResponse<TFields extends object = {}, T extends Content = Content> {
  contents: Array<Omit<T, "values"> & { values: TFields }>;
  options?: GetMixedContentsOptions<TFields>;
  pagination?: PaginationInfo;
}

export interface GetMixedContentsOptions<TFields extends object> extends GetContentsOptionsBase, QueryOptionsBase {
  fields?: (keyof TFields)[];
}

type MixedContents<T extends Content, TFields extends object> = Array<Omit<T, "values"> & { values: TFields }>;

/**
 * Same method as `getContents()` only without content fields type safety.
 */
export const getContentsMixed = <T extends Content, TFields extends object>(
  source: GetContentsSource<T>,
  core: XcoreCore,
  fields: Array<keyof TFields> = [],
  options: GetMixedContentsOptions<TFields> = {}
): BaseApiRequest<MixedContentsResponse<TFields, T>> => {
  const url = removeDuplicateQueryParams(
    getContentsUrl<T, TFields>(core, source, {
      ...options,
      includeNonLocalized: true,
      ...resolveLocaleParams(core, options?.locales),
      ...(fields.length > 0 && { fields, includeValues: true }),
    })
  );

  return core
    .fetch(url)
    .then(paginateRes)
    .then(async ([contents, pagination]: [MixedContents<T, TFields>, PaginationParsed]) => {
      const contentsWithUnfetchedValuesResolved = await resolveUnfetchedValuesToContentValuesForContents(contents as Content[], core);
      const contentsWithSubCategoriesResolved = resolveContentsSubCategories(contentsWithUnfetchedValuesResolved);

      return {
        contents: contentsWithSubCategoriesResolved as MixedContents<T, TFields>,
        options,
        ...resolvePagination(pagination, options?.perPage),
      };
    });
};

/**
 * Constructs a URL for fetching content based on the provided context and options.
 *
 * @param source.types Content type or array of content types.
 * @param source.ids Content ID or array of content IDs.
 * @param options An object containing options for fetching content, such as sorting, categorization, and filtering.
 *
 * @returns A string representing the URL for fetching content, including query parameters for sorting, categorization, filtering, pagination, and context-specific options.
 */
const getContentsUrl = <T extends Content, TFields extends object>(
  core: XcoreCore,
  source: GetContentsSource<T>,
  options: GetMixedContentsOptions<TFields>
) => getContentsUrlFunc(core, source, options as GetContentsOptions<T>);
